import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

/**
 * Motion primitives.
 *
 * Two rules hold everywhere:
 *
 * Nothing animates in from invisible. Each of these starts from a visible
 * resting state and only shifts position or opacity slightly, so the page is
 * complete in its first painted frame -- what a screenshot, a shared link and
 * a reader who never scrolls all get. A section parked at opacity 0 waiting on
 * an observer is a blank page to anyone whose observer never fires.
 *
 * prefers-reduced-motion is honoured by dropping the movement, not the
 * content. For someone who gets motion sick, a page that slides is not a nicer
 * page, and one that hides content until it moves is a broken one.
 */

const EASE = [0.22, 1, 0.36, 1]; // gentle deceleration; nothing bounces

export function FadeUp({ children, delay = 0, className = "", as = "div" }) {
  const reduce = useReducedMotion();
  const Tag = motion[as] || motion.div;
  return (
    <Tag
      className={className}
      initial={reduce ? false : { opacity: 0.001, y: 16 }}
      whileInView={reduce ? {} : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, delay, ease: EASE }}
    >
      {children}
    </Tag>
  );
}

/** Staggers its children. Pair with FadeIn for a list or card row. */
export function Stagger({ children, className = "", gap = 0.07 }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? false : "hidden"}
      whileInView="shown"
      viewport={{ once: true, margin: "-60px" }}
      variants={{ hidden: {}, shown: { transition: { staggerChildren: gap } } }}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({ children, className = "" }) {
  return (
    <motion.div
      className={className}
      variants={{
        hidden: { opacity: 0.001, y: 14 },
        shown: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE } },
      }}
    >
      {children}
    </motion.div>
  );
}

/** A card that lifts slightly under the cursor. Pointer devices only. */
export function Lift({ children, className = "" }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      whileHover={reduce ? {} : { y: -4 }}
      whileTap={reduce ? {} : { y: -1 }}
      transition={{ duration: 0.2, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

/**
 * A number that counts up when it comes into view.
 *
 * Renders the final value immediately when motion is reduced, and always ends
 * on the exact number rather than wherever the easing left it.
 */
export function CountUp({ to, suffix = "", duration = 1.1, className = "", format }) {
  const reduce = useReducedMotion();
  const fmt = format || ((n) => n.toLocaleString());
  if (reduce) return <span className={className}>{fmt(to)}{suffix}</span>;
  return (
    <motion.span
      className={className}
      initial={{ opacity: 0.001 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
    >
      <Counter to={to} duration={duration} format={fmt} />{suffix}
    </motion.span>
  );
}

function Counter({ to, duration, format }) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let frame;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / (duration * 1000));
      // easeOutCubic: fast first, settles gently on the final figure
      setValue(Math.round(to * (1 - Math.pow(1 - t, 3))));
      if (t < 1) frame = requestAnimationFrame(tick);
      else setValue(to);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [to, duration]);
  return <>{format(value)}</>;
}


/* -------------------------------------------------------------------------
 * In-app motion
 *
 * Different rules apply inside a tool someone uses all day. Durations are
 * roughly half the marketing ones, because a transition you enjoy on a first
 * visit is one you resent on the fortieth. Nothing here delays input: motion
 * is on appearance and position only, never on a control's response to a
 * click.
 *
 * The rule that matters most is that lists animate when their contents
 * change, not when they are refetched. A poll that re-staggers thirty rows
 * every few seconds is the single most irritating thing motion can do to a
 * dashboard, so entrance is keyed on identity.
 * ---------------------------------------------------------------------- */

const APP_EASE = [0.32, 0.72, 0, 1];
const APP_DURATION = 0.22;

/** Page-level transition. Wrap a route's content once, at the top. */
export function PageTransition({ children, routeKey }) {
  const reduce = useReducedMotion();
  if (reduce) return <>{children}</>;
  return (
    <motion.div
      key={routeKey}
      initial={{ opacity: 0.001, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: APP_DURATION, ease: APP_EASE }}
    >
      {children}
    </motion.div>
  );
}

/**
 * A list whose rows appear once.
 *
 * `settled` should be false only while the first load is in flight. Once true
 * the stagger has run and later updates change content in place, so a refresh
 * does not replay the animation.
 */
export function AppList({ children, className = "", settled = true, as = "div" }) {
  const reduce = useReducedMotion();
  const Tag = motion[as] || motion.div;
  const Plain = as;
  if (reduce) return <Plain className={className}>{children}</Plain>;
  return (
    <Tag
      className={className}
      initial={settled ? false : "hidden"}
      animate="shown"
      variants={{ hidden: {}, shown: { transition: { staggerChildren: 0.035 } } }}
    >
      {children}
    </Tag>
  );
}

export function AppRow({ children, className = "", as = "div", ...rest }) {
  const Tag = motion[as] || motion.div;
  return (
    <Tag
      className={className}
      {...rest}
      variants={{
        hidden: { opacity: 0.001, y: 6 },
        shown: { opacity: 1, y: 0, transition: { duration: APP_DURATION, ease: APP_EASE } },
      }}
    >
      {children}
    </Tag>
  );
}

/** Cross-fades between loading and loaded without the layout jumping. */
export function Swap({ show, children, className = "" }) {
  const reduce = useReducedMotion();
  return (
    <AnimatePresence mode="wait" initial={false}>
      {show ? (
        <motion.div
          key="content"
          className={className}
          initial={reduce ? false : { opacity: 0.001 }}
          animate={{ opacity: 1 }}
          exit={reduce ? {} : { opacity: 0.001 }}
          transition={{ duration: 0.16 }}
        >
          {children}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
